import { config } from "../Environment";
import type { CaptureKind, CaptureLocale } from "./capture";
import type { MmObjectType } from "./mentalModels";
import type { MmScriptId } from "../lib/diveDeeper";

export type NowMode = "everyday" | "advance" | "respond" | "steady" | "grow";
export type NowLinkKind = "project" | "journey" | "trait" | "mental_model" | "activity" | "insight" | "action";
export interface NowConnection { kind: NowLinkKind; id: string; label: string; why: string }

/** The saved item a session dives into (spec 2026-11-04 §2.2). */
export interface NowItemRef { kind: CaptureKind; id: string; mmType?: MmObjectType }
export type NowMainRoute =
  | "breakdown" | "guided_activity" | "mm_script" | "mm_general" | "project_room" | "project_step" | "trait_panel" | "underneath";
export interface NowMainDto { route: NowMainRoute; pathId?: MmScriptId; steps?: number }
export type NowToolKind = "mm_operator" | "next_step" | "activity";
export interface NowToolDto { tool: NowToolKind; operator?: string; refId?: string; label?: string }
export interface NowQuickDto extends NowToolDto { why: string; minutes: number }
export type NowChangedKind = "steps_added" | "activity_created" | "model_updated";
export interface NowChanged { kind: NowChangedKind; count?: number }
export type NowHandoffRoute =
  | "belief_audit" | "fear_shrinker" | "decision_detox" | "identity_update" | "mm_general" | "project_room" | "underneath" | "activity" | "connection";

export interface NowSessionDto {
  _id: string;
  mode: NowMode;
  status: "open" | "done" | "abandoned";
  itemRef: NowItemRef | null;
  connections: NowConnection[];
  main: NowMainDto[];
  quick: NowQuickDto | null;
  more: NowToolDto[];
  suggested: "main" | "quick";
  /** Route key the person usually starts with (spec §4), when it moved the badge. */
  usual: string | null;
  broughtIn: { kind: NowLinkKind; id: string }[];
  handoff: { route: NowHandoffRoute; at: number } | null;
  changed: NowChanged[];
  stepText: string | null;
  wisdomLine: string | null;
  proposal: { kind: "project" | "trait"; title: string } | null;
  followUps: { kind: NowLinkKind; id: string; label: string }[];
}
export interface NowTodayItem { sessionId: string; stepText: string; stepId: string }
export interface NowResume { sessionId: string; note: string; route: NowHandoffRoute }

async function call<T>(retryFetch: Function, path: string, init: RequestInit = {}): Promise<T> {
  const response = await retryFetch(`${config.API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  if (!response.ok) {
    let code = response.statusText;
    try {
      const data = await response.json();
      if (data && data.error) code = data.error;
    } catch {
      /* keep statusText */
    }
    throw new Error(`${path} failed: ${code}`);
  }
  return response.status === 204 ? (undefined as T) : response.json();
}
const post = <T>(retryFetch: Function, path: string, body: unknown = {}) =>
  call<T>(retryFetch, path, { method: "POST", body: JSON.stringify(body) });

export const startNow = (
  retryFetch: Function,
  body: { captureId: string; mode?: NowMode; itemRef: NowItemRef; reflective: boolean; locale: CaptureLocale }
) => post<{ session: NowSessionDto }>(retryFetch, "/api/now/start", body);
export const finishNow = (retryFetch: Function, id: string, body: { tool: string; result?: NowChanged; answer?: string }) =>
  post<{ session: NowSessionDto }>(retryFetch, `/api/now/${id}/finish`, body);
export const bringNow = (retryFetch: Function, id: string, body: { kind: NowLinkKind; id: string; on: boolean }) =>
  post<{ broughtIn: { kind: NowLinkKind; id: string }[] }>(retryFetch, `/api/now/${id}/bring`, body);
export const handoffNow = (retryFetch: Function, id: string, body: { route: NowHandoffRoute }) =>
  post<void>(retryFetch, `/api/now/${id}/handoff`, body);
export const keepNow = (retryFetch: Function, id: string, body: { stepText: string; create?: boolean }) =>
  post<{ stepId: string; createdId?: string }>(retryFetch, `/api/now/${id}/keep`, body);
export const abandonNow = (retryFetch: Function, id: string) => post<void>(retryFetch, `/api/now/${id}/abandon`);
export const reflectNow = (retryFetch: Function, id: string, body: { tried: "yes" | "partly" | "no"; note?: string }) =>
  post<{ insightId?: string }>(retryFetch, `/api/now/${id}/reflect`, body);
export const nowToday = (retryFetch: Function) =>
  call<{ items: NowTodayItem[]; resume: NowResume | null }>(retryFetch, `/api/now/today?tzOffset=${new Date().getTimezoneOffset()}`);
